/**
 * Preview URL Sync Hook
 *
 * Syncs the side panel preview state with URL search params.
 * Enables shareable URLs for expanded preview overlays.
 *
 * URL params:
 * - preview: item ID
 * - type: 'table' | 'linked_table'
 * - expanded: 'true' when overlay is expanded
 *
 * @module features/workspace/hooks/usePreviewUrlSync
 */

import { useEffect, useRef, useCallback } from 'react';
import { useRouter } from '@tanstack/react-router';
import type { WorkspaceItemType } from '@loader/shared-types';

import { useSidePanelStore } from '../stores/useSidePanelStore';
import { useSelectionStore } from '../stores/useSelectionStore';
import type { WorkspaceSearchParams } from '../utils/workspaceSearchSchema';

/**
 * Props for usePreviewUrlSync hook
 */
interface UsePreviewUrlSyncProps {
  /** Current search params from route */
  searchParams: WorkspaceSearchParams;
}

/**
 * Hook to sync side panel preview state with URL
 *
 * - On mount: reads URL params and restores preview state
 * - On state change: updates URL params
 */
export function usePreviewUrlSync({
  searchParams,
}: UsePreviewUrlSyncProps) {
  const router = useRouter();
  const isRestoringFromUrl = useRef(false);
  // Track which preview we've already restored to avoid re-processing
  const lastRestoredPreviewRef = useRef<string | null>(null);
  // Track if this is the very first render (for URL sync effect)
  const hasRunUrlSyncRef = useRef(false);

  // Get store state and actions
  const isOpen = useSidePanelStore((s) => s.isOpen);
  const isExpanded = useSidePanelStore((s) => s.isExpanded);
  const previewItem = useSidePanelStore((s) => s.previewItem);
  const mode = useSidePanelStore((s) => s.mode);
  const openPreview = useSidePanelStore((s) => s.openPreview);
  const expand = useSidePanelStore((s) => s.expand);
  const close = useSidePanelStore((s) => s.close);

  // Selection store for syncing item selection
  const selectItem = useSelectionStore((s) => s.selectItem);

  /**
   * Update URL search params while staying on current route
   */
  const updateUrl = useCallback(
    (newPreviewId?: string, newType?: WorkspaceItemType, newExpanded?: boolean) => {
      // Build new search params, preserving non-preview params
      const { tag, folder } = searchParams;
      const newSearch: Record<string, string | boolean | undefined> = {
        ...(tag && { tag }),
        ...(folder && { folder }),
      };

      // Add preview params if provided
      if (newPreviewId && newType) {
        newSearch.preview = newPreviewId;
        newSearch.type = newType;
        if (newExpanded) {
          newSearch.expanded = true;
        }
      }

      // Update URL using router.navigate with current location
      // This keeps us on the same route, only updating search params
      void router.navigate({
        to: '.',
        search: newSearch,
        replace: true,
      });
    },
    [router, searchParams.tag, searchParams.folder]
  );

  /**
   * Restore preview state from URL when searchParams change
   *
   * Uses lastRestoredPreviewRef to track which preview we've already processed,
   * so we can handle navigation between folders (component reuse) correctly.
   */
  useEffect(() => {
    const { preview, type, expanded } = searchParams;

    // If URL has preview params, restore the state
    if (preview && type && (type === 'table' || type === 'linked_table')) {
      // Skip if we already restored this exact preview
      if (lastRestoredPreviewRef.current === preview) {
        return;
      }

      lastRestoredPreviewRef.current = preview;
      isRestoringFromUrl.current = true;

      // Open preview with minimal item data
      // Full data will be fetched by PreviewPanel
      openPreview({
        id: preview,
        type: type as 'table' | 'linked_table',
        name: '', // Will be populated when data loads
      });

      // Also select the item in the folder view
      selectItem(preview);

      // Expand if URL indicates expanded state
      if (expanded) {
        expand();
      }

      // Reset flag after a tick
      setTimeout(() => {
        isRestoringFromUrl.current = false;
      }, 0);
    } else {
      // No preview in URL - clear the ref so future previews work
      lastRestoredPreviewRef.current = null;
    }
  }, [searchParams, openPreview, expand, selectItem]);

  /**
   * Sync URL when side panel state changes
   */
  useEffect(() => {
    // Skip if we're restoring from URL (avoid infinite loop)
    if (isRestoringFromUrl.current) return;

    // Skip first run to allow URL → state restore to happen first
    if (!hasRunUrlSyncRef.current) {
      hasRunUrlSyncRef.current = true;
      return;
    }

    // Only sync for preview modes (not create table)
    if (mode === 'previewTable' || mode === 'previewLinkedTable') {
      if (isOpen && previewItem) {
        updateUrl(
          previewItem.id,
          previewItem.type,
          isExpanded
        );
      }
    } else if (!isOpen) {
      // Panel closed - clear URL params
      updateUrl();
    }
  }, [isOpen, isExpanded, previewItem, mode, updateUrl]);

  /**
   * Handle closing the panel - also clears URL
   */
  const handleClose = useCallback(() => {
    close();
    updateUrl();
  }, [close, updateUrl]);

  return {
    handleClose,
  };
}
